import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import { Paper, Stack, TablePagination, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { downloadFile } from "src/utils";
import {
  useExportSalesTransactions,
  useGetSalesTransactions,
} from "src/api/sales";
import { IClientResponse } from "src/types/clients";
import { ICity } from "src/types/generic";

const tableHeadText = [
  "ID",
  "Date",
  "Location",
  "Service",
  "Entry Tickets",
  "Exit Tickets",
  "Paid Tickets",
  "Free Tickets",
  "Cash Payments",
  "Card Payments",
  "Online Payments",
  "Total Sales",
];

interface IProps {
  selectedLocations: any[];
  startDate: Date | null;
  endDate: Date | null;
  selectedService: string;
  selectedClient?: IClientResponse;
  selectedCity?: ICity;
  locationType?: string;
}

const SalesTable = ({
  selectedLocations,
  selectedService,
  startDate,
  endDate,
  selectedClient,
  selectedCity,
  locationType,
}: IProps) => {
  const [_, setNotifications] = useRecoilState(notificationsState);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filters = {
    ...(selectedClient && { userId: selectedClient.id }),
    ...(selectedLocations.length > 0 && {
      locations: selectedLocations.map((loc) => loc.id),
    }),
    ...(selectedCity && { cities: [selectedCity.id] }),
    ...(selectedService !== "all" && { serviceType: selectedService }),
    ...(locationType !== "all" && { locationType }),
    ...(startDate && { startDate: startDate?.getTime() }),
    ...(endDate && { endDate: endDate?.getTime() }),
  };

  // APIs
  const { data, isLoading } = useGetSalesTransactions({
    ...filters,
    page: page + 1,
    limit: rowsPerPage,
  });

  const { isFetching, refetch } = useExportSalesTransactions(filters);

  const handleExport = () => {
    refetch().then((res) => {
      if (res.status === "success" && res.data) {
        const utf8CSVData = `\uFEFF${res.data}`;
        downloadFile(utf8CSVData, "text/csv", "transactions-details");
        setNotifications((prev) => [
          ...prev,
          {
            type: "success",
            message: "Downloaded successfully",
          },
        ]);
      }
    });
  };

  const hasData = data?.data?.length > 0;

  //return to 1st page after filters change
  useEffect(() => {
    setPage(0);
  }, [selectedLocations, startDate, endDate, selectedService, selectedClient]);

  useEffect(() => {
    if (data) {
      setTotalCount(data?.meta.total);
    }
  }, [data]);

  const serviceLabels: Record<string, string> = {
    Valet: "Valet",
    "Golf Cart": "Golf Cart",
    partialCarWash: "Partial Car Wash",
    fullCarWash: "Complete Car Wash",
    SelfParking: "Self Parking",
  };

  return (
    <Paper
      sx={{
        bgcolor: "#242424",
        boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
        padding: { xs: "15px", md: "30px" },
        maxWidth: { xs: "100%", md: `calc(100vw - 360px)` },
        // boxSizing: "content-box"
      }}
    >
      <Stack direction="row" justifyContent="space-between" mb={3}>
        <Typography fontSize={"20px"} fontWeight={700}>
          Transactions Details
        </Typography>
        {hasData ? (
          <LoadingButton
            variant="contained"
            sx={{ gap: 0.5, width: "100px" }}
            onClick={handleExport}
            loading={isFetching}
          >
            Export
          </LoadingButton>
        ) : null}
      </Stack>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {tableHeadText.map((item) => (
                <TableCell
                  key={item}
                  sx={{
                    fontWeight: 600,
                    color: "#CC9C33",
                  }}
                >
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableLoadingSkeleton rowsLength={rowsPerPage} cellsLength={12} />
          ) : (
            <>
              {hasData ? (
                <TableBody>
                  {data?.data.map((row: any, index: number) => (
                    <TableRow
                      key={`${row.totalSales}_${index}`}
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                      }}
                    >
                      <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.location}</TableCell>
                      <TableCell>{serviceLabels[row.service]}</TableCell>
                      <TableCell>{row.ticketEntryCount}</TableCell>
                      <TableCell>{row.ticketExitCount}</TableCell>
                      <TableCell>{row.ticketPaidCount}</TableCell>
                      <TableCell>{row.freeTicketCount}</TableCell>
                      <TableCell>{row.totalCashSales}</TableCell>
                      <TableCell>{row.totalCardSales}</TableCell>
                      <TableCell>{row.totalOnlineSales}</TableCell>
                      <TableCell>{row.totalSales}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : null}
            </>
          )}
        </Table>

        {!hasData && !isLoading && <NoData />}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
        />
      </TableContainer>
    </Paper>
  );
};
export default SalesTable;
